import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, CircularProgress, Alert, Switch, FormControlLabel, Select, MenuItem, InputLabel, FormControl, Box, Typography, Avatar } from '@mui/material';
import { Driver } from '../models/Driver'; // Import the Driver interface

interface DriverFormDialogProps {
  open: boolean;
  onClose: () => void;
  driver?: Driver; // Optional: if provided, the form is for editing
  onSubmit: (driverData: any, photoFile?: File | null) => Promise<void>; // Function to handle form submission, now accepts photoFile
  loading: boolean;
  error: string | null;
}

const DriverFormDialog: React.FC<DriverFormDialogProps> = ({
  open,
  onClose,
  driver,
  onSubmit,
  loading,
  error,
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    cin: '',
    address: '',
    vehicleType: '',
    vehicleRegistration: '',
    enabled: true, // Default to enabled for new drivers
    is_favorite: false, // Default to not favorite for new drivers
    driverPourcentage: 100, // Default driver payout percentage
    location: {
      latitude: '',
      longitude: ''
    }
  });
  const [photoFile, setPhotoFile] = useState<File | undefined | null>(undefined); // State for the selected photo file (can be File, undefined, or null for removal)
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | undefined>(undefined); // State for photo preview

  useEffect(() => {
    // Populate form data if editing an existing driver
    if (driver) {
      setFormData({
        firstName: driver.firstName,
        lastName: driver.lastName,
        phone: driver.phone,
        email: driver.email ?? '',
        cin: driver.cin ?? '',
        address: driver.address ?? '',
        vehicleType: driver.vehicleType ?? '',
        vehicleRegistration: driver.vehicleRegistration ?? '',
        enabled: driver.enabled,
        is_favorite: driver.is_favorite,
        driverPourcentage: driver.driverPourcentage ?? 100, // Populate driverPourcentage if editing, default to 100
        location: {
          latitude: driver.location?.latitude?.toString() ?? '',
          longitude: driver.location?.longitude?.toString() ?? ''
        }
      });
      setPhotoFile(undefined); // Clear selected file when opening for edit
      setPhotoPreviewUrl(driver.photoUrl ?? undefined); // Set initial preview from existing photoUrl
    } else {
      // Reset form data for adding a new driver
      setFormData({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        cin: '',
        address: '',
        vehicleType: '',
        vehicleRegistration: '',
        enabled: true,
        is_favorite: false,
        driverPourcentage: 100, // Reset driverPourcentage for new driver form
        location: {
          latitude: '',
          longitude: ''
        }
      });
      setPhotoFile(undefined); // Clear selected file
      setPhotoPreviewUrl(undefined); // Clear preview
    }
  }, [open, driver]); // Reset/populate when dialog opens or driver prop changes

  // Effect to create a preview URL when a file is selected
  useEffect(() => {
    if (photoFile instanceof File) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(photoFile);
    } else if (driver?.photoUrl && photoFile === undefined) {
       // If no new file is selected (photoFile is undefined) but there's an existing photoUrl, show it
       setPhotoPreviewUrl(driver.photoUrl);
    } else if (photoFile === null || (!photoFile && !driver?.photoUrl)) {
       // If photoFile is explicitly null (marked for removal) or no file and no existing photoUrl, clear preview
       setPhotoPreviewUrl(undefined);
    }
  }, [photoFile, driver?.photoUrl]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ 
      ...formData, 
      location: {
        ...formData.location,
        [name]: value
      }
    });
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData({
            ...formData,
            location: {
              latitude: position.coords.latitude.toString(),
              longitude: position.coords.longitude.toString()
            }
          });
        },
        (error) => {
          console.error('Error getting location:', error);
          alert('Impossible d\'obtenir la localisation actuelle. Veuillez vérifier les permissions.');
        }
      );
    } else {
      alert('La géolocalisation n\'est pas supportée par ce navigateur.');
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
  };

   const handleSelectChange = (e: any) => { // Use 'any' for SelectChangeEvent
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setPhotoFile(e.target.files[0]);
    } else {
      setPhotoFile(undefined); // Clear selected file if input is cleared
    }
  };

  const handleRemovePhoto = () => {
     setPhotoFile(null); // Explicitly set to null to indicate photo removal
     setPhotoPreviewUrl(undefined); // Clear the preview
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!formData.firstName || !formData.lastName || !formData.phone) {
      alert('Prénom, Nom et Téléphone sont requis.');
      return;
    }
    
    // Convert driverPourcentage to number and handle location before submission
    // Only include location if both latitude and longitude are provided
    const dataToSubmit = {
      ...formData,
      driverPourcentage: Number(formData.driverPourcentage),
      location: (formData.location.latitude && formData.location.longitude) ? {
        latitude: Number(formData.location.latitude),
        longitude: Number(formData.location.longitude)
      } : undefined
    };
    
    // Pass formData and photoFile (or null if removing) to the onSubmit prop
    await onSubmit(dataToSubmit, photoFile);
  };

  const dialogTitle = driver ? 'Modifier le Livreur' : 'Ajouter un Nouveau Livreur';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          autoFocus
          margin="dense"
          name="firstName"
          label="Prénom"
          type="text"
          fullWidth
          variant="outlined"
          value={formData.firstName}
          onChange={handleChange}
          required
        />
        <TextField
          margin="dense"
          name="lastName"
          label="Nom"
          type="text"
          fullWidth
          variant="outlined"
          value={formData.lastName}
          onChange={handleChange}
          required
        />
        <TextField
          margin="dense"
          name="phone"
          label="Téléphone"
          type="tel"
          fullWidth
          variant="outlined"
          value={formData.phone}
          onChange={handleChange}
          required
        />
         <TextField
          margin="dense"
          name="email"
          label="Email (Facultatif)"
          type="email"
          fullWidth
          variant="outlined"
          value={formData.email}
          onChange={handleChange}
        />
         <TextField
          margin="dense"
          name="cin"
          label="CIN (Facultatif)"
          type="text"
          fullWidth
          variant="outlined"
          value={formData.cin}
          onChange={handleChange}
        />
         <TextField
          margin="dense"
          name="address"
          label="Adresse (Facultatif)"
          type="text"
          fullWidth
          variant="outlined"
          value={formData.address}
          onChange={handleChange}
        />
        <FormControl fullWidth margin="dense">
           <InputLabel id="vehicle-type-label">Type de Véhicule</InputLabel>
           <Select
             labelId="vehicle-type-label"
             name="vehicleType"
             value={formData.vehicleType}
             label="Type de Véhicule"
             onChange={handleSelectChange}
           >
             <MenuItem value=""><em>Sélectionner un type</em></MenuItem>
             <MenuItem value="motorcycle">Moto</MenuItem>
             <MenuItem value="car">Voiture</MenuItem>
             <MenuItem value="bicycle">Vélo</MenuItem>
             <MenuItem value="other">Autre</MenuItem>
           </Select>
         </FormControl>
         <TextField
          margin="dense"
          name="vehicleRegistration"
          label="Immatriculation du Véhicule (Facultatif)"
          type="text"
          fullWidth
          variant="outlined"
          value={formData.vehicleRegistration}
          onChange={handleChange}
        />

        {/* Driver Percentage Field */}
        <TextField
          margin="dense"
          name="driverPourcentage"
          label="Commission (%)"
          type="number"
          fullWidth
          variant="outlined"
          value={formData.driverPourcentage}
          onChange={handleChange}
          inputProps={{ min: 0, max: 100 }} // Restrict input to 0-100
          helperText="Entrez le pourcentage (0-100) de commission que le livreur recevra."
        />

        {/* Location Section - OPTIMIZED FOR FIRESTORE COSTS */}
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Localisation (Facultatif)
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            La localisation permet d'optimiser l'attribution des livraisons mais n'est pas obligatoire. 
            Elle peut être mise à jour ultérieurement via l'application mobile du livreur.
          </Alert>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
            <TextField
              margin="dense"
              name="latitude"
              label="Latitude"
              type="number"
              variant="outlined"
              value={formData.location.latitude}
              onChange={handleLocationChange}
              inputProps={{ step: "any" }}
              sx={{ flex: 1 }}
            />
            <TextField
              margin="dense"
              name="longitude"
              label="Longitude"
              type="number"
              variant="outlined"
              value={formData.location.longitude}
              onChange={handleLocationChange}
              inputProps={{ step: "any" }}
              sx={{ flex: 1 }}
            />
            <Button
              variant="outlined"
              onClick={getCurrentLocation}
              sx={{ minWidth: 'auto', px: 2 }}
            >
              📍 Position actuelle
            </Button>
          </Box>
          {formData.location.latitude && formData.location.longitude && (
            <Typography variant="caption" color="text.secondary">
              Coordonnées: {formData.location.latitude}, {formData.location.longitude}
            </Typography>
          )}
        </Box>

        {/* Photo Upload Section */}
        <Box sx={{ mt: 2, mb: 2 }}>
           <Typography variant="subtitle1" gutterBottom>Photo du Livreur</Typography>
           <input
             accept="image/*"
             style={{ display: 'none' }}
             id="upload-photo"
             type="file"
             onChange={handlePhotoChange}
           />
           <label htmlFor="upload-photo">
             <Button variant="outlined" component="span">
               {photoFile ? 'Changer la Photo' : (photoPreviewUrl ? 'Changer la Photo' : 'Télécharger une Photo')}
             </Button>
           </label>
           {photoPreviewUrl && (
             <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                <Avatar src={photoPreviewUrl} sx={{ width: 56, height: 56, mr: 2 }} />
                <Button variant="text" color="error" onClick={handleRemovePhoto}>
                   Supprimer la Photo
                </Button>
             </Box>
           )}
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={formData.enabled}
              onChange={handleSwitchChange}
              name="enabled"
              color="primary"
            />
          }
          label="Activé"
        />
         <FormControlLabel
          control={
            <Switch
              checked={formData.is_favorite}
              onChange={handleSwitchChange}
              name="is_favorite"
              color="primary"
            />
          }
          label="Favori"
        />

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Annuler
        </Button>
        <Button onClick={handleSubmit} color="primary" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : (driver ? 'Enregistrer les Modifications' : 'Ajouter le Livreur')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DriverFormDialog;
